import React, { useState, useEffect, useRef } from 'react';
import '../styles/ToolsPage.css';
import PaginationNav from '../components/PaginationNav';
import ToolEditor from '../components/ToolEditor';
import EmbeddingStatusIndicator from '../components/EmbeddingStatusIndicator';
import { useGroup } from '../contexts/GroupContext';
import { API_BASE_URL, getAuthHeaders, getEmbeddingRequests, requestEmbedding, removeEmbeddingsForTool, getEmbeddingSummary, listMcpServers, testMcpServer, getPlatformToolDefaults, enablePlatformToolDefaults } from '../utils/api';
import { 
  enhancedFetch, 
  clearErrorAfterDelay,
  generateErrorMessage,
  normalizeToolKind
} from '../utils/errorHandling';
import { extractEmbeddingRequestError, buildErrorDisplay } from '../utils/errorDetails';
import { getWebSearchProviderLabel } from '../utils/webSearchProviders';
import {
  getEffectivePollInterval,
  initActivityTracking,
  onActivityTierReset,
} from '../utils/activityAwarePolling';
import ToolRecommendationsBanner from '../components/ToolRecommendationsBanner';

function toolKindLabel(kind) {
  const normalized = normalizeToolKind(kind);
  if (normalized === 'analytics') return 'Analytics';
  if (normalized === 'filter') return 'Filter';
  if (normalized === 'web_search') return 'Web search';
  if (normalized === 'research') return 'Deep Research';
  if (normalized === 'tool_chain') return 'Tool chain';
  if (normalized === 'scheduler') return 'Scheduler';
  if (normalized === 'http_request') return 'HTTP request';
  if (normalized === 'webpage_scrape') return 'Webpage scrape';
  if (normalized === 'entry_update') return 'Entry update';
  if (normalized === 'entry_create') return 'Entry create';
  if (normalized === 'mcp_call') return 'MCP call';
  if (normalized === 'email_draft') return 'Email draft';
  return 'Search';
}

function isNonEmbeddingToolKind(kind) {
  const normalized = normalizeToolKind(kind);
  return (
    normalized === 'analytics' ||
    normalized === 'web_search' ||
    normalized === 'filter' ||
    normalized === 'research' ||
    normalized === 'tool_chain' ||
    normalized === 'scheduler' ||
    normalized === 'http_request' ||
    normalized === 'webpage_scrape' ||
    normalized === 'entry_update' ||
    normalized === 'entry_create' ||
    normalized === 'mcp_call' ||
    normalized === 'email_draft'
  );
}

function ToolsPage() {
  const { activeSuffix } = useGroup();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '', details: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toolToDelete, setToolToDelete] = useState(null);
  const [showRemoveEmbeddingsConfirm, setShowRemoveEmbeddingsConfirm] = useState(false);
  const [toolToRemoveEmbeddings, setToolToRemoveEmbeddings] = useState(null);
  const [removingEmbeddingsId, setRemovingEmbeddingsId] = useState(null);
  // Track retries without triggering re-renders
  const retryCountRef = useRef(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const toolsLoadedRef = useRef(false);
  const toolsLoadingRef = useRef(false);
  const errorTimeoutRef = useRef(null);
  
  // Embedding status state; single list-only poll, 20s when active / 120s when idle, only when tab visible
  const [embeddingStatuses, setEmbeddingStatuses] = useState(new Map());
  const [embeddingLoading, setEmbeddingLoading] = useState(false);
  const [pollingErrors, setPollingErrors] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const pollingErrorsRef = useRef(0);
  const pollTimeoutRef = useRef(null);
  const lastPollBaseMsRef = useRef(120000);
  /** Faster while embeddings run so request metadata (total/remaining) updates feel responsive */
  const POLL_ACTIVE_MS = 10000;
  const POLL_IDLE_MS = 120000;
  // Per-tool progress info and a 1s ticker for smooth time updates
  const [progressByTool, setProgressByTool] = useState(new Map());
  // const [secondTicker, setSecondTicker] = useState(0);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 50,
    totalEntries: 0,
    totalPages: 0
  });
  const [pageSize, setPageSize] = useState(50);

  const [mcpServers, setMcpServers] = useState({ owned: [], public: [] });
  const [mcpLoading, setMcpLoading] = useState(false);
  const [mcpTestingId, setMcpTestingId] = useState(null);
  const [mcpDebugById, setMcpDebugById] = useState({});
  const [platformDefaults, setPlatformDefaults] = useState(null);
  const [platformDefaultsLoading, setPlatformDefaultsLoading] = useState(false);
  const [enablingDefaults, setEnablingDefaults] = useState(false);

  const refreshPlatformDefaults = async () => {
    setPlatformDefaultsLoading(true);
    try {
      const data = await getPlatformToolDefaults(activeSuffix || undefined);
      setPlatformDefaults(data);
    } catch (_) {
      setPlatformDefaults(null);
    } finally {
      setPlatformDefaultsLoading(false);
    }
  };

  useEffect(() => {
    setMcpLoading(true);
    listMcpServers()
      .then((data) => setMcpServers({ owned: data.owned || [], public: data.public || [] }))
      .catch(() => setMcpServers({ owned: [], public: [] }))
      .finally(() => setMcpLoading(false));
  }, []);

  useEffect(() => {
    refreshPlatformDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSuffix]);

  useEffect(() => {
    // Prevent duplicate API calls (e.g., from React StrictMode or rapid tab switching)
    if (!toolsLoadedRef.current && !toolsLoadingRef.current) {
      toolsLoadedRef.current = true;
      toolsLoadingRef.current = true;
      loadTools(0).finally(() => {
        toolsLoadingRef.current = false;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Utility: humanize age text such as "5m ago"
  const getAgeText = (dateStr) => {
    if (!dateStr) return null;
    const then = new Date(dateStr).getTime();
    if (isNaN(then)) return null;
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.floor(hr / 24);
    return `${d}d ago`;
  };

  // Helper to compute per-tool embedding status focusing on the tool's search_field
  const computeStatusForTool = (tool, requests) => {
    if (!tool || !tool.id) return { status: 'none', request: null, error: null, ageText: null };
    const searchKey = tool.search_field;
    const groupSlug = tool?.ingestion_group_slug || tool?.group || tool?.group_slug || null;
    const list = Array.isArray(requests) ? requests : [];

    // First, try strict match: belongs to this tool AND matches the tool's search field key
    let matches = list.filter(r => {
      const sameTool = r?.metadata?.source === tool.id;
      const sameKey = !searchKey || r?.key === searchKey;
      const sameGroup = !groupSlug || r?.metadata?.group_slug === groupSlug;
      return sameTool && sameKey && sameGroup;
    });

    // If nothing matched and we do have a searchKey, fall back to key-only match.
    // Some listing payloads do not include metadata.source, so we infer by key.
    if ((!matches || matches.length === 0) && searchKey) {
      matches = list.filter(r => {
        const sameKey = r?.key === searchKey;
        const sameGroup = !groupSlug || r?.metadata?.group_slug === groupSlug;
        return sameKey && sameGroup;
      });
    }

    // If still nothing and there's no searchKey configured, try source-only as a last resort
    if ((!matches || matches.length === 0) && !searchKey) {
      matches = list.filter(r => {
        const sameTool = r?.metadata?.source === tool.id;
        const sameGroup = !groupSlug || r?.metadata?.group_slug === groupSlug;
        return sameTool && sameGroup;
      });
    }
    if (!matches || matches.length === 0) {
      return { status: 'none', request: null, error: null, ageText: null };
    }
    const parseTime = (r) => new Date(r?.updated_at || r?.created_at || 0).getTime();
    // Prefer active ones if any
    const active = matches.filter(r => r?.status === 'created' || r?.status === 'processing').sort((a, b) => parseTime(b) - parseTime(a));
    if (active.length > 0) {
      const req = active[0];
      return { status: req.status, request: req, error: null, ageText: null };
    }
    // Then prefer finished ones
    const finished = matches.filter(r => r?.status === 'finished').sort((a, b) => parseTime(b) - parseTime(a));
    if (finished.length > 0) {
      const req = finished[0];
      return { status: 'finished', request: req, error: null, ageText: getAgeText(req.updated_at || req.created_at) };
    }
    // Otherwise, pick the newest errored/canceled to reflect state
    const errored = matches.filter(r => r?.status === 'errored').sort((a, b) => parseTime(b) - parseTime(a));
    if (errored.length > 0) {
      const req = errored[0];
      return {
        status: 'errored',
        request: req,
        error: extractEmbeddingRequestError(req) || 'Embedding failed',
        ageText: null
      };
    }
    const canceled = matches.filter(r => r?.status === 'canceled').sort((a, b) => parseTime(b) - parseTime(a));
    if (canceled.length > 0) {
      const req = canceled[0];
      return { status: 'canceled', request: req, error: null, ageText: null };
    }
    // Fallback none
    const newest = matches.sort((a, b) => parseTime(b) - parseTime(a))[0];
    return { status: newest?.status || 'none', request: newest || null, error: null, ageText: null };
  };

  // Fetch embedding status for all tools (based on their search field)
  // First try the lightweight summary endpoint, then fall back to the full listing
  const loadEmbeddingStatuses = async (toolsList) => {
    if (!toolsList || toolsList.length === 0) {
      return;
    }

    setEmbeddingLoading(true);
    
    try {
      // 1) Initialize all tools with 'none' and try the lightweight summary endpoint first
      const provisional = new Map();
      toolsList.forEach(tool => {
        if (!tool?.id) return;
        provisional.set(tool.id, { status: 'none', request: null, error: null, ageText: null });
      });

      // Attempt to fetch summary per tool (when we know the search_field)
      // Note: We don't yet have a concrete value context; we still call to gather lastEmbeddedAt when possible.
      // If the endpoint requires a value and rejects, we safely ignore and keep 'none'.
      const summaries = await Promise.all(
        toolsList.map(async (tool) => {
          if (!tool?.id || !tool?.search_field) return { id: tool?.id, ok: false };
          try {
            const group = tool?.ingestion_group_slug || tool?.group || tool?.group_slug;
            const sum = await getEmbeddingSummary({ key: tool.search_field, value: '', group });
            return { id: tool.id, ok: true, summary: sum };
          } catch (_) {
            return { id: tool.id, ok: false };
          }
        })
      );

      summaries.forEach(({ id, ok, summary }) => {
        if (!id) return;
        if (ok && summary) {
          const ageText = summary.lastEmbeddedAt ? getAgeText(summary.lastEmbeddedAt) : null;
          if (summary.needsReembed) {
            provisional.set(id, {
              status: 'stale',
              request: null,
              error: null,
              ageText,
            });
          } else if (summary.lastEmbeddedAt) {
            // Mark as ready based on summary (may be refined by active listing below)
            provisional.set(id, {
              status: 'finished',
              request: null,
              error: null,
              ageText,
            });
          }
        }
      });

      // 2) Fetch full requests to detect any active/errored states and refine statuses
      // Scope listing by group if all tools share the same group; otherwise no group filter
      const uniqueGroups = Array.from(new Set((toolsList || []).map(t => t?.ingestion_group_slug).filter(Boolean)));
      const groupFilter = uniqueGroups.length === 1 ? uniqueGroups[0] : undefined;
      const response = await getEmbeddingRequests(undefined, groupFilter);
      if (response.success && response.requests) {
        const newStatuses = new Map(provisional);

        toolsList.forEach(tool => {
          if (!tool?.id) return;
          const computed = computeStatusForTool(tool, response.requests);
          // If the listing shows active/errored, prefer it; otherwise keep summary-derived finished if present
          const current = newStatuses.get(tool.id);
          const isActive = computed.status === 'created' || computed.status === 'processing';
          const isErrorOrCanceled = computed.status === 'errored' || computed.status === 'canceled';
          if (isActive || isErrorOrCanceled) {
            newStatuses.set(tool.id, computed);
          } else if (computed.status === 'finished') {
            // Preserve stale (ingest newer than last embed) from summary when listing only shows finished
            if (current?.status === 'stale') {
              newStatuses.set(tool.id, {
                ...computed,
                status: 'stale',
                ageText: current.ageText || computed.ageText,
              });
            } else {
              newStatuses.set(tool.id, computed);
            }
          } else if (!current) {
            newStatuses.set(tool.id, { status: 'none', request: null, error: null, ageText: null });
          }
        });

        setEmbeddingStatuses(newStatuses);
        startPollingForActiveRequests(newStatuses);
      } else {
        // Could not fetch listing; keep provisional summary-based statuses
        console.error('Failed to load embedding statuses:', response);
        setEmbeddingStatuses(provisional);
      }
    } catch (error) {
      console.error('Error loading embedding statuses:', error);
      // Set fallback state for error handling
      const fallbackStatuses = new Map();
      toolsList.forEach(tool => {
        if (!tool?.id) return;
        fallbackStatuses.set(tool.id, {
          status: 'none',
          request: null,
          error: 'Failed to load status',
          ageText: null
        });
      });
      setEmbeddingStatuses(fallbackStatuses);
    } finally {
      setEmbeddingLoading(false);
    }
  };

  const scheduleNextPoll = (delayMs) => {
    if (typeof document !== 'undefined' && document.hidden) return;
    lastPollBaseMsRef.current = delayMs;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    pollTimeoutRef.current = setTimeout(() => {
      pollTimeoutRef.current = null;
      pollEmbeddingStatuses();
    }, getEffectivePollInterval(delayMs));
  };

  // Start or reschedule polling: 20s when any active, 120s when idle
  const startPollingForActiveRequests = (statusMap) => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    let hasActive = false;
    statusMap.forEach((statusInfo) => {
      if (statusInfo.status === 'created' || statusInfo.status === 'processing') hasActive = true;
    });
    scheduleNextPoll(hasActive ? POLL_ACTIVE_MS : POLL_IDLE_MS);
  };

  // List-only poll: one GET /embedding, derive all tool statuses from response
  const pollEmbeddingStatuses = async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    const toolsList = tools;
    if (!toolsList?.length) {
      scheduleNextPoll(POLL_IDLE_MS);
      return;
    }
    try {
      const uniqueGroups = Array.from(new Set(toolsList.map(t => t?.ingestion_group_slug).filter(Boolean)));
      const groupFilter = uniqueGroups.length === 1 ? uniqueGroups[0] : undefined;
      const response = await getEmbeddingRequests(undefined, groupFilter);
      if (!response.success || !response.requests) {
        scheduleNextPoll(POLL_IDLE_MS);
        return;
      }
      pollingErrorsRef.current = 0;
      setPollingErrors(0);
      setConnectionStatus('connected');

      const computedById = new Map();
      toolsList.forEach((tool) => {
        if (!tool?.id) return;
        computedById.set(tool.id, computeStatusForTool(tool, response.requests));
      });

      let hasActive = false;
      const nextProgress = new Map();
      computedById.forEach((computed, toolId) => {
        if (computed.status === 'created' || computed.status === 'processing') {
          hasActive = true;
          const md = computed.request?.metadata;
          const total = typeof md?.total === 'number' ? md.total : null;
          const remaining = typeof md?.remaining === 'number' ? md.remaining : null;
          const startedAt = computed.request?.created_at || computed.request?.updated_at || null;
          if (total != null && total > 0 && remaining != null) {
            nextProgress.set(toolId, {
              total,
              embedded: Math.max(0, total - remaining),
              startedAt,
            });
          } else {
            nextProgress.set(toolId, {
              total: 0,
              embedded: 0,
              startedAt,
              waitingCounts: true,
            });
          }
        }
      });

      setEmbeddingStatuses((prevStatuses) => {
        const newStatuses = new Map(prevStatuses);
        computedById.forEach((computed, id) => newStatuses.set(id, computed));
        return newStatuses;
      });

      if (hasActive) setProgressByTool(nextProgress);
      else setProgressByTool(new Map());

      scheduleNextPoll(hasActive ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    } catch (error) {
      console.error('Error polling embedding statuses:', error);
      pollingErrorsRef.current += 1;
      setPollingErrors(pollingErrorsRef.current);
      if (pollingErrorsRef.current === 1) setConnectionStatus('retrying');
      else if (pollingErrorsRef.current >= 3) setConnectionStatus('error');
      scheduleNextPoll(POLL_IDLE_MS);
    }
  };

  // Get embedding status for a specific tool
  const getToolEmbeddingStatus = (toolId) => {
    const statusInfo = embeddingStatuses.get(toolId);
    if (!statusInfo) {
      return embeddingLoading ? 'loading' : 'none';
    }
    return statusInfo.status;
  };

  const showToolError = (error, operation, response = null) => {
    const { summary, details } = buildErrorDisplay(error, response);
    const text = generateErrorMessage(error, operation, response) || summary;
    setMessage({ type: 'error', text, details: details || (summary !== text ? summary : null) });
    errorTimeoutRef.current = clearErrorAfterDelay(() => {
      setMessage({ type: '', text: '', details: null });
    }, 10000);
  };

  // Get embedding error for a specific tool
  const getToolEmbeddingError = (toolId) => {
    const statusInfo = embeddingStatuses.get(toolId);
    return statusInfo?.error || null;
  };

  // Get embedding newest age text for a specific tool
  const getToolEmbeddingAge = (toolId) => {
    const statusInfo = embeddingStatuses.get(toolId);
    return statusInfo?.ageText || null;
  };

  // Check if tool has an active embedding request
  const hasActiveEmbeddingRequest = (toolId) => {
    const status = getToolEmbeddingStatus(toolId);
    return status === 'created' || status === 'processing';
  };

  // Handle initiate embedding button click
  const handleInitiateEmbedding = async (tool) => {
    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    try {
      const group = tool?.ingestion_group_slug || tool?.group || tool?.group_slug || '';
      if (!group) {
        setMessage({ type: 'error', text: 'Please set an ingestion group for this tool before initiating embeddings.' });
        // Auto-clear error message
        errorTimeoutRef.current = clearErrorAfterDelay(() => {
          setMessage({ type: '', text: '' });
        }, 6000);
        return;
      }
      const response = await requestEmbedding(tool.id, group);
      
      if (response.success && response.request) {
        setMessage({ 
          type: 'success', 
          text: `Embedding request created successfully for "${tool.name}"` 
        });
        
        // Update the embedding status and let the single polling loop pick it up
        const newStatuses = new Map(embeddingStatuses);
        newStatuses.set(tool.id, {
          status: response.request.status,
          request: response.request,
          error: null,
          ageText: null
        });
        setEmbeddingStatuses(newStatuses);
        startPollingForActiveRequests(newStatuses);
        
        // Auto-clear success message
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        throw new Error(response.error || response.message || 'Failed to create embedding request');
      }
    } catch (error) {
      console.error('Error initiating embedding:', error);
      showToolError(error, 'creating embedding request', error.response);
    }
  };

  const loadTools = async (pageNum = 0, currentPageSize = null, isRetryAttempt = false) => {
    setLoading(true);
    if (isRetryAttempt) {
      setIsRetrying(true);
    }
    
    // Clear any existing error messages
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setMessage({ type: '', text: '' });
    
    const effectivePageSize = currentPageSize || pageSize;
    
    try {
      // Build URL with pagination parameters (Vector Tools API)
      const url = new URL(`${API_BASE_URL}/casc/v1/api/vector/tools`);
      url.searchParams.set('page', (pageNum + 1).toString()); // API expects 1-based page numbers
      url.searchParams.set('pageSize', effectivePageSize.toString());

      const authHeaders = await getAuthHeaders();
      const data = await enhancedFetch(url.toString(), {
        method: 'GET',
        headers: authHeaders
      }, {
        maxRetries: 2,
        baseDelay: 1000,
        errorType: 'network'
      });

      if (data.success && data.items) {
        // Normalize each tool to ensure ingestion_group_slug is populated even when
        // the API provides it under metadata.group_slug or _context.groupSlug
        const normalizedItems = (Array.isArray(data.items) ? data.items : []).map((t) => {
          const slugs = Array.isArray(t?.ingestion_group_slugs)
            ? t.ingestion_group_slugs
            : (Array.isArray(t?.metadata?.group_slugs) ? t.metadata.group_slugs : []);
          const groupSlug = slugs[0]
            || t?.ingestion_group_slug
            || t?.metadata?.group_slug
            || t?._context?.groupSlug
            || t?.group
            || t?.group_slug
            || '';
          const ingestion_group_slugs = slugs.length ? slugs : (groupSlug ? [groupSlug] : []);
          return { ...t, ingestion_group_slug: groupSlug, ingestion_group_slugs };
        });
        setTools(normalizedItems);
        retryCountRef.current = 0; // Reset retry count on success
        
        // Update pagination state based on response
        setPagination({
          page: pageNum,
          pageSize: effectivePageSize,
          totalEntries: data.total || 0,
          totalPages: data.totalPages || Math.max(1, Math.ceil((data.total || 0) / effectivePageSize))
        });
        
        // Load embedding statuses for the loaded tools
        const toolsList = normalizedItems.filter(t => t && t.id);
        if (toolsList.length > 0) {
          loadEmbeddingStatuses(toolsList);
        }
      } else {
        throw new Error('Invalid response format: missing items data');
      }
    } catch (error) {
      console.error('Error loading tools:', error);
      showToolError(error, 'loading tools', error.response);
      
      // Set empty state on error to avoid showing stale data
      setTools([]);
      setPagination({
        page: 0,
        pageSize: effectivePageSize,
        totalEntries: 0,
        totalPages: 0
      });
    } finally {
      setLoading(false);
      setIsRetrying(false);
    }
  };

  const handleCreateTool = () => {
    setEditingTool(null);
    setShowModal(true);
  };

  const handleEditTool = (tool) => {
    setEditingTool(tool);
    setShowModal(true);
  };

  const handleDeleteClick = (toolId) => {
    setToolToDelete(toolId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!toolToDelete) {
      return;
    }

    setShowDeleteConfirm(false);
    const toolId = toolToDelete;
    const toolName = tools.find(t => (t.id || t.slug) === toolId)?.name || 'Unknown Tool';
    setToolToDelete(null);

    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    try {
      console.log(`[ToolsPage] Deleting tool:`, toolId);

      // Vector Tools delete endpoint. Include group context if known.
      const toolGroup = (tools.find(t => (t.id || t.slug) === toolId)?.ingestion_group_slug) || '';
      const deleteUrl = new URL(`${API_BASE_URL}/casc/v1/api/vector/tools/${encodeURIComponent(toolId)}`);
      if (toolGroup) deleteUrl.searchParams.set('group', toolGroup);

      const authHeaders = await getAuthHeaders();
      const data = await enhancedFetch(deleteUrl.toString(), {
        method: 'DELETE',
        headers: authHeaders
      }, {
        maxRetries: 1, // Don't retry delete operations
        baseDelay: 1000
      });

      console.log(`[ToolsPage] Delete response data:`, data);

      if (data.success && (data.deleted === true || data.deleted > 0)) {
        setMessage({ type: 'success', text: `Tool "${toolName}" deleted successfully` });
        
        // Auto-clear success message and reload
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
          loadTools(pagination.page);
        }, 1500);
      } else if (data.success && data.deleted === 0) {
        setMessage({ type: 'error', text: `Tool "${toolName}" not found or already deleted` });
        
        // Auto-clear error message and reload to refresh the list
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
          loadTools(pagination.page);
        }, 3000);
      } else {
        throw new Error(data.error || 'Delete operation failed');
      }
    } catch (error) {
      console.error('[ToolsPage] Exception deleting tool:', error);
      
      const errorMessage = generateErrorMessage(error, 'deleting tool', error.response);
      setMessage({ type: 'error', text: errorMessage });
      
      // Auto-clear error message
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 8000);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setToolToDelete(null);
  };

  const handleRemoveEmbeddingsClick = (tool) => {
    setToolToRemoveEmbeddings(tool);
    setShowRemoveEmbeddingsConfirm(true);
  };

  const handleRemoveEmbeddingsCancel = () => {
    setShowRemoveEmbeddingsConfirm(false);
    setToolToRemoveEmbeddings(null);
  };

  const handleRemoveEmbeddingsConfirm = async () => {
    const tool = toolToRemoveEmbeddings;
    if (!tool?.id) {
      handleRemoveEmbeddingsCancel();
      return;
    }

    const group = tool?.ingestion_group_slug || tool?.group || tool?.group_slug || '';
    const toolName = tool?.name || 'Tool';

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    if (!group) {
      setMessage({ type: 'error', text: 'Set an ingestion group on this tool before removing embeddings.' });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 6000);
      return;
    }

    setRemovingEmbeddingsId(tool.id);
    try {
      const data = await removeEmbeddingsForTool(tool.id, group);
      setShowRemoveEmbeddingsConfirm(false);
      setToolToRemoveEmbeddings(null);
      if (data.skipped && data.message) {
        setMessage({ type: 'success', text: data.message });
      } else {
        const emb = typeof data.embeddingsDeleted === 'number' ? data.embeddingsDeleted : 0;
        const req = typeof data.requestsDeleted === 'number' ? data.requestsDeleted : 0;
        setMessage({
          type: 'success',
          text: `Removed ${emb} embedding row(s) and ${req} request row(s) for "${toolName}" (${group}).`
        });
      }
      const toolsList = tools.filter((t) => t && t.id);
      if (toolsList.length > 0) {
        loadEmbeddingStatuses(toolsList);
      }
      errorTimeoutRef.current = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } catch (error) {
      console.error('Error removing embeddings:', error);
      const errorMessage = generateErrorMessage(error, 'removing embeddings', error.response);
      setMessage({ type: 'error', text: errorMessage });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '' });
      }, 8000);
    } finally {
      setRemovingEmbeddingsId(null);
    }
  };

  const handleSaveTool = async (formData) => {
    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    try {
      const kind = normalizeToolKind(formData.tool_kind);
      const researchAction =
        kind === 'research'
          ? (formData.research_action || 'start')
          : formData.research_action;
      const toolData = editingTool
        ? {
            id: editingTool.id, // Required for update
            name: formData.name,
            description: formData.description || null,
            tool_kind: kind,
            search_field: (formData.search_field || '').trim(),
            return_fields: formData.return_fields || [],
            search_provider: formData.search_provider || 'brave',
            research_action: researchAction,
            default_mode: formData.default_mode || null,
            default_provider: formData.default_provider || null,
            default_model: formData.default_model || null,
            failure_policy: formData.failure_policy || 'stop',
            chain_steps: formData.chain_steps || [],
            period_hours: formData.period_hours ?? 24,
            cron_expr: formData.cron_expr || null,
            prompt_id: formData.prompt_id || null,
            prompt_instructions: formData.prompt_instructions || null,
            http_method: formData.http_method || 'GET',
            request_url: formData.request_url || '',
            http_headers: formData.http_headers || {},
            auth_type: formData.auth_type || 'none',
            auth_token: formData.auth_token || '',
            auth_header_name: formData.auth_header_name || 'X-Api-Key',
            body_template: formData.body_template || '',
            timeout_ms: formData.timeout_ms ?? 20000,
            max_body_chars: formData.max_body_chars ?? 200000,
            response_as: formData.response_as || 'auto',
            batch_size: formData.batch_size ?? 1,
            mcp_server_id: formData.mcp_server_id || null,
            mcp_tool_name: formData.mcp_tool_name || null,
            default_arguments: formData.default_arguments || null,
            email_action: formData.email_action || 'save',
            default_tone: formData.default_tone || null,
            ingestion_group_slug: (formData.ingestion_group_slugs && formData.ingestion_group_slugs[0]) || formData.ingestion_group_slug || '',
            ingestion_group_slugs: formData.ingestion_group_slugs || [],
            suffix: formData.suffix || editingTool.suffix || activeSuffix, // Include suffix
            is_private: formData.is_private !== false,
            yuml_config: formData.yuml_config || null,
            allow_create: formData.allow_create !== false,
          }
        : {
            slug: formData.slug,
            name: formData.name,
            description: formData.description || null,
            tool_kind: kind,
            search_field: (formData.search_field || '').trim(),
            return_fields: formData.return_fields || [],
            search_provider: formData.search_provider || 'brave',
            research_action: researchAction,
            default_mode: formData.default_mode || null,
            default_provider: formData.default_provider || null,
            default_model: formData.default_model || null,
            failure_policy: formData.failure_policy || 'stop',
            chain_steps: formData.chain_steps || [],
            period_hours: formData.period_hours ?? 24,
            cron_expr: formData.cron_expr || null,
            prompt_id: formData.prompt_id || null,
            prompt_instructions: formData.prompt_instructions || null,
            http_method: formData.http_method || 'GET',
            request_url: formData.request_url || '',
            http_headers: formData.http_headers || {},
            auth_type: formData.auth_type || 'none',
            auth_token: formData.auth_token || '',
            auth_header_name: formData.auth_header_name || 'X-Api-Key',
            body_template: formData.body_template || '',
            timeout_ms: formData.timeout_ms ?? 20000,
            max_body_chars: formData.max_body_chars ?? 200000,
            response_as: formData.response_as || 'auto',
            batch_size: formData.batch_size ?? 1,
            mcp_server_id: formData.mcp_server_id || null,
            mcp_tool_name: formData.mcp_tool_name || null,
            default_arguments: formData.default_arguments || null,
            email_action: formData.email_action || 'save',
            default_tone: formData.default_tone || null,
            ingestion_group_slug: (formData.ingestion_group_slugs && formData.ingestion_group_slugs[0]) || formData.ingestion_group_slug || '',
            ingestion_group_slugs: formData.ingestion_group_slugs || [],
            suffix: formData.suffix || activeSuffix, // Include suffix for new tools
            is_private: formData.is_private !== false,
            yuml_config: formData.yuml_config || null,
            allow_create: formData.allow_create !== false,
          };

      // Vector Tools API endpoints: create and update
      const endpoint = editingTool
        ? `${API_BASE_URL}/casc/v1/api/vector/tools/update`
        : `${API_BASE_URL}/casc/v1/api/vector/tools`;

      const method = 'POST'; // Both create and update use POST
      const action = editingTool ? 'updating' : 'creating';

      console.log(`[ToolsPage] ${action.charAt(0).toUpperCase() + action.slice(1)} tool:`, {
        endpoint,
        toolData,
        action
      });

      const authHeaders = await getAuthHeaders();
      const data = await enhancedFetch(endpoint, {
        method: method,
        headers: authHeaders,
        body: JSON.stringify({
          ...toolData,
          // Provide group aliases for server convenience
          group: toolData.ingestion_group_slug,
          group_slug: toolData.ingestion_group_slug,
          ingestion_group_slug: toolData.ingestion_group_slug,
          ingestion_group_slugs: toolData.ingestion_group_slugs,
          group_slugs: toolData.ingestion_group_slugs,
        })
      }, {
        maxRetries: 1, // Don't retry create/update operations to avoid duplicates
        baseDelay: 1000
      });

      console.log(`[ToolsPage] Response data:`, data);

      if (data.success && (data.item || data.row)) {
        const toolName = formData.name || 'Tool';
        const createdScheduler =
          !editingTool && normalizeToolKind(formData.tool_kind) === 'scheduler';
        setMessage({ 
          type: 'success', 
          text: createdScheduler
            ? `Scheduler "${toolName}" created (inactive). Open Settings → Scheduler and set it Active to run.`
            : `Tool "${toolName}" ${editingTool ? 'updated' : 'created'} successfully`
        });
        
        // Auto-clear success message and close modal
        errorTimeoutRef.current = setTimeout(() => {
          setMessage({ type: '', text: '' });
          setShowModal(false);
          setEditingTool(null);
          loadTools(pagination.page);
        }, createdScheduler ? 4000 : 1500);
      } else {
        throw new Error(data.error || data.message || `${action} operation failed`);
      }
    } catch (error) {
      console.error('[ToolsPage] Exception saving tool:', error);
      console.error('[ToolsPage] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        type: error.type,
        status: error.status
      });
      if (error.debug) {
        console.warn('[ToolsPage] Tool field validation debug (server snapshot)', error.debug);
      }

      let errorMessage = generateErrorMessage(error, editingTool ? 'updating tool' : 'creating tool', error.response);
      if (error.debug?.suggestions?.length) {
        const s0 = error.debug.suggestions[0];
        if (s0?.try_fields?.length) {
          errorMessage += ` Suggested: ${s0.try_fields.join(', ')}.`;
        }
      }
      if (error.debug) {
        errorMessage += ' See details below and browser console for full key comparison.';
      }
      const { details } = buildErrorDisplay(error, error.response);
      setMessage({ type: 'error', text: errorMessage, details });
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setMessage({ type: '', text: '', details: null });
      }, 10000);
      
      throw error;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTool(null);
  };

  // Retry handler for failed operations
  const handleRetry = () => {
    retryCountRef.current += 1;
    loadTools(pagination.page, pageSize, true);
  };

  // Visibility: pause polling when tab hidden, run one poll when visible
  useEffect(() => {
    initActivityTracking();
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      } else {
        pollEmbeddingStatuses();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const unsubscribeTier = onActivityTierReset(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      scheduleNextPoll(lastPollBaseMsRef.current || POLL_IDLE_MS);
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unsubscribeTier();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollingErrorsRef.current = 0;
    };
  }, []);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    const pageIndex = newPage - 1; // Convert from 1-based to 0-based
    if (pageIndex >= 0 && pageIndex < pagination.totalPages) {
      loadTools(pageIndex);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    // Reset to first page when changing page size
    loadTools(0, newPageSize);
  };

  return (
    <div className="tools-page">
      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          <div className="alert-content">
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            <div className="alert-text-block">
              <span className="alert-text">{message.text}</span>
              {message.details && (
                <pre className="alert-error-details">{message.details}</pre>
              )}
            </div>
          </div>
          {message.type === 'error' && !loading && !isRetrying && (
            <button 
              className="btn btn-sm btn-outline-primary retry-btn"
              onClick={handleRetry}
              title="Retry the failed operation"
            >
              <i className="fas fa-redo"></i> Retry
            </button>
          )}
        </div>
      )}
      {connectionStatus !== 'connected' && (
        <div className={`alert alert-${connectionStatus === 'retrying' ? 'warning' : 'error'}`} style={{ marginBottom: '20px' }}>
          <div className="alert-content">
            <i className={`fas fa-${connectionStatus === 'retrying' ? 'exclamation-triangle' : 'wifi'}`}></i>
            <span className="alert-text">
              {connectionStatus === 'retrying'
                ? 'Connection issue detected. Retrying…'
                : `Connection error (${pollingErrors} failed attempts). Retrying…`}
            </span>
          </div>
        </div>
      )}
      <div className="tool-manager">
        <div className="tool-header">
          <h1><i className="fas fa-tools"></i> Vector Tools Manager</h1>
          <p className="tool-description">
            Manage vector search tools for your project. Create and configure tools to search and retrieve data from your vector database.
          </p>
        </div>

        {!platformDefaultsLoading && platformDefaults?.needs_setup && (
          <ToolRecommendationsBanner
            title="Recommended default capabilities"
            recommendations={(platformDefaults.capabilities || [])
              .filter((c) => c.action === 'seed')
              .map((c) => ({
                id: c.id,
                kind: c.kind,
                title: c.label,
                message: `${c.description}${c.missing_seeds?.length ? ` Missing: ${c.missing_seeds.join(', ')}.` : ''}`,
                reason: c.status,
              }))}
            enabling={enablingDefaults}
            onEnableDefaults={async () => {
              setEnablingDefaults(true);
              try {
                const result = await enablePlatformToolDefaults({
                  suffix: activeSuffix || undefined,
                  ids: (platformDefaults.capabilities || [])
                    .filter((c) => c.action === 'seed')
                    .map((c) => c.id),
                });
                const n = Array.isArray(result.created) ? result.created.length : 0;
                const errN = Array.isArray(result.errors) ? result.errors.length : 0;
                setMessage({
                  type: errN && !n ? 'error' : 'success',
                  text: errN && !n
                    ? `Could not create defaults: ${result.errors[0]?.error || 'unknown error'}`
                    : `Created ${n} default tool${n === 1 ? '' : 's'}${errN ? ` (${errN} skipped with errors)` : ''}. Add them to each project’s tool allowlist if the project uses a restricted list.`,
                  details: null,
                });
                await refreshPlatformDefaults();
                await loadTools(pagination.page, pageSize, true);
              } catch (err) {
                setMessage({
                  type: 'error',
                  text: err?.message || 'Failed to enable platform defaults',
                  details: null,
                });
              } finally {
                setEnablingDefaults(false);
              }
            }}
          />
        )}

        {!platformDefaultsLoading && platformDefaults && !platformDefaults.needs_setup && (
          <div className="platform-defaults-ok" style={{ marginBottom: 16, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            <i className="fas fa-check-circle" style={{ marginRight: 6 }} />
            Recommended defaults (webpage scrape, Deep Research) are present on this horizon.
            Account web search, email drafts, lock-record, and embedding helpers are injected automatically in chat.
          </div>
        )}

        <div className="mcp-tooling-section">
          <h2><i className="fas fa-plug" /> MCP Tooling</h2>
          <p className="section-description">
            Connected MCP servers for first-class MCP call tools, agents, and feel-lucky web search.
            Use <strong>Test</strong> to load live tool lists for diagnosis. Create an <strong>MCP call</strong> tool below to use them in chat.
          </p>
          {mcpLoading ? (
            <div className="loading"><i className="fas fa-spinner fa-spin" /> Loading MCP servers...</div>
          ) : (
            <>
              {[...(mcpServers.owned || []), ...(mcpServers.public || [])].length === 0 ? (
                <p className="mcp-tooling-empty">No MCP servers configured yet. Add one in Settings → User → MCP Servers.</p>
              ) : (
                <div className="mcp-tooling-grid">
                  {[...(mcpServers.owned || []), ...(mcpServers.public || [])].map((server) => {
                    const debug = mcpDebugById[server.id];
                    return (
                      <div className="mcp-tooling-card" key={server.id}>
                        <h3>
                          {server.name}
                          {server.isPublic && <span className="tool-slug" style={{ marginLeft: 8 }}>PUBLIC</span>}
                          {!server.isActive && <span className="tool-slug" style={{ marginLeft: 8 }}>OFF</span>}
                        </h3>
                        <div className="mcp-tooling-meta">
                          <span><strong>Transport:</strong> {String(server.transport || '').toUpperCase()}</span>
                          <span><strong>Slug:</strong> {server.slug}</span>
                          {server.scopeProjectId && <span><strong>Scope:</strong> {server.scopeProjectId}</span>}
                          {server.readOnly && <span><strong>Mode:</strong> Read-only</span>}
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={mcpTestingId === server.id}
                            onClick={async () => {
                              setMcpTestingId(server.id);
                              try {
                                const result = await testMcpServer(server.id);
                                setMcpDebugById((prev) => ({
                                  ...prev,
                                  [server.id]: {
                                    ok: true,
                                    ...result,
                                  },
                                }));
                              } catch (err) {
                                setMcpDebugById((prev) => ({
                                  ...prev,
                                  [server.id]: {
                                    ok: false,
                                    error: err.message || String(err),
                                  },
                                }));
                              } finally {
                                setMcpTestingId(null);
                              }
                            }}
                          >
                            {mcpTestingId === server.id
                              ? <><i className="fas fa-spinner fa-spin" /> Testing…</>
                              : <><i className="fas fa-plug" /> Test / debug</>}
                          </button>
                        </div>
                        {debug && (
                          <pre
                            style={{
                              marginTop: 10,
                              maxHeight: 180,
                              overflow: 'auto',
                              fontSize: 11,
                              whiteSpace: 'pre-wrap',
                              background: '#0f172a',
                              color: '#e2e8f0',
                              padding: 8,
                              borderRadius: 8,
                            }}
                          >
{JSON.stringify(
  debug.ok === false
    ? { success: false, error: debug.error }
    : {
        success: true,
        toolCount: debug.toolCount,
        durationMs: debug.durationMs,
        transport: debug.transport,
        tools: (debug.tools || []).map((t) => ({
          name: t.name,
          description: t.description || '',
        })),
      },
  null,
  2
)}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="tool-actions">
          <button
            className="btn btn-primary"
            onClick={handleCreateTool}
          >
            <i className="fas fa-plus"></i> Create Tool
          </button>
        </div>

        {loading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i> 
            {isRetrying ? 'Retrying...' : 'Loading tools...'}
          </div>
        ) : (
          <div className="tool-sections">
            {tools.length > 0 ? (
              <div className="tool-section">
                <h2><i className="fas fa-tools"></i> Available Tools</h2>
                
                {/* Top Pagination Controls */}
                {pagination.totalPages > 1 && (
                  <PaginationNav
                    position="top"
                    page={pagination.totalPages > 0 ? pagination.page + 1 : 1}
                    pageSize={pageSize}
                    total={pagination.totalEntries}
                    allowedPageSizes={[10, 20, 50, 100]}
                    disabled={loading || pagination.totalPages === 0}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                )}
                
                <div className="tool-grid">
                  {tools.map((tool) => (
                    <div key={tool.id || tool.slug} className="tool-card">
                      <div className="tool-card-header">
                        <h3 className="tool-title">{tool.name}</h3>
                        <span className="tool-slug">{tool.slug}</span>
                      </div>
                      <p className="tool-description">{tool.description || 'No description'}</p>
                      <div className="tool-meta">
                        <span><strong>Type:</strong> {toolKindLabel(tool.tool_kind)}</span>
                        {normalizeToolKind(tool.tool_kind) === 'web_search' && (
                          <span>
                            <strong>Provider:</strong>{' '}
                            {getWebSearchProviderLabel(tool.metadata?.provider)}
                          </span>
                        )}
                        {normalizeToolKind(tool.tool_kind) === 'research' && (
                          <span>
                            <strong>Action:</strong>{' '}
                            {(() => {
                              const raw = tool.metadata?.research_action;
                              const slug = String(tool.slug || tool.name || '').toLowerCase();
                              if (raw === 'report' || raw === 'status' || raw === 'start') return raw;
                              if (/(^|[_-])(fetch|report)([_-]|$)/.test(slug)) return 'report';
                              if (/(^|[_-])status([_-]|$)/.test(slug)) return 'status';
                              return raw || 'start';
                            })()}
                          </span>
                        )}
                        {normalizeToolKind(tool.tool_kind) === 'tool_chain' && (
                          <span>
                            <strong>Steps:</strong>{' '}
                            {Array.isArray(tool.metadata?.steps) ? tool.metadata.steps.length : 0}
                            {' · '}
                            {tool.metadata?.failure_policy === 'continue' ? 'continue on failure' : 'stop on failure'}
                          </span>
                        )}
                        {normalizeToolKind(tool.tool_kind) === 'scheduler' && (
                          <span>
                            <strong>Cron:</strong>{' '}
                            {tool.metadata?.cron_expr || `${tool.metadata?.period_hours || 24}h`}
                            {tool.metadata?.prompt_id
                              ? ` · prompt ${tool.metadata.prompt_id}`
                              : ` · ${Array.isArray(tool.metadata?.steps) ? tool.metadata.steps.length : 0} step(s)`}
                          </span>
                        )}
                        {normalizeToolKind(tool.tool_kind) === 'http_request' && (
                          <span>
                            <strong>HTTP:</strong>{' '}
                            {tool.metadata?.method || 'GET'}{' '}
                            {tool.metadata?.request_url || '—'}
                            {tool.metadata?.auth_type && tool.metadata.auth_type !== 'none'
                              ? ` · auth ${tool.metadata.auth_type}`
                              : ''}
                            {` · batch ${tool.metadata?.batch_size || 1}`}
                          </span>
                        )}
                        {normalizeToolKind(tool.tool_kind) === 'webpage_scrape' && (
                          <span>
                            <strong>Scrape:</strong>{' '}
                            browser-like fetch
                            {tool.metadata?.timeout_ms ? ` · ${tool.metadata.timeout_ms}ms` : ''}
                            {tool.metadata?.max_chars ? ` · max ${tool.metadata.max_chars} chars` : ''}
                          </span>
                        )}
                        <span>
                          <strong>Ingestion Group{Array.isArray(tool.ingestion_group_slugs) && tool.ingestion_group_slugs.length > 1 ? 's' : ''}:</strong>{' '}
                          {Array.isArray(tool.ingestion_group_slugs) && tool.ingestion_group_slugs.length > 0
                            ? tool.ingestion_group_slugs.join(', ')
                            : (tool.ingestion_group_slug || '—')}
                        </span>
                        {normalizeToolKind(tool.tool_kind) !== 'research'
                          && normalizeToolKind(tool.tool_kind) !== 'tool_chain'
                          && normalizeToolKind(tool.tool_kind) !== 'scheduler'
                          && normalizeToolKind(tool.tool_kind) !== 'http_request'
                          && normalizeToolKind(tool.tool_kind) !== 'webpage_scrape' && (
                          <>
                            <span><strong>Search Field:</strong> {tool.search_field || 'Not set'}</span>
                            <span><strong>Return Fields:</strong> {tool.return_fields?.length || 0} fields</span>
                          </>
                        )}
                        <span>
                          <strong>Visibility:</strong>{' '}
                          {tool.is_private ? (
                            <>
                              <i className="fas fa-lock" style={{ color: '#9c27b0', marginRight: '4px' }} aria-hidden />
                              Private <span className="tool-meta-hint">(admins and owner)</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-globe" style={{ marginRight: '4px', opacity: 0.85 }} aria-hidden />
                              Public
                            </>
                          )}
                        </span>
                        <span><strong>Created:</strong> {tool.created_at ? new Date(tool.created_at).toLocaleDateString() : 'Unknown'}</span>
                        <div className="embedding-status-container">
                          <strong>Embedding Status:</strong>
                          <EmbeddingStatusIndicator 
                            status={getToolEmbeddingStatus(tool.id)}
                            error={getToolEmbeddingError(tool.id)}
                            ageText={getToolEmbeddingAge(tool.id)}
                          />
                          {getToolEmbeddingStatus(tool.id) === 'errored' && embeddingStatuses.get(tool.id)?.request?.id && (
                            <div className="embedding-request-meta">
                              Request: {embeddingStatuses.get(tool.id).request.id}
                            </div>
                          )}
                          {/* Progress/ETA for active processing */}
                          {(() => {
                            const s = embeddingStatuses.get(tool.id);
                            const isActive = s && (s.status === 'created' || s.status === 'processing');
                            const prog = progressByTool.get(tool.id);
                            if (!isActive || !prog) return null;
                            if (prog.waitingCounts) {
                              return (
                                <div className="tool-embedding-progress tep-queued" aria-label="Embedding queued">
                                  <span className="tep-counts">
                                    <i className="fas fa-hourglass-half" aria-hidden />
                                    {' '}Starting… counts appear once the worker picks up this request.
                                  </span>
                                </div>
                              );
                            }
                            if (!prog.total || prog.total <= 0) return null;
                            const now = Date.now();
                            const startedMs = prog.startedAt ? new Date(prog.startedAt).getTime() : null;
                            const elapsedMs = startedMs ? Math.max(0, now - startedMs) : 0;
                            const done = Math.max(0, Math.min(prog.total, prog.embedded || 0));
                            const remaining = Math.max(0, prog.total - done);
                            const pct = prog.total > 0 ? Math.min(100, Math.max(0, (done / prog.total) * 100)) : 0;
                            const avgPerItemMs = done > 0 ? (elapsedMs / done) : null;
                            const etaMs = avgPerItemMs ? Math.round(avgPerItemMs * remaining) : null;

                            const fmt = (ms) => {
                              if (ms == null) return '—';
                              const sTot = Math.floor(ms / 1000);
                              const h = Math.floor(sTot / 3600);
                              const m = Math.floor((sTot % 3600) / 60);
                              const sRem = sTot % 60;
                              if (h > 0) return `${h}h ${m}m`;
                              if (m > 0) return `${m}m ${sRem}s`;
                              return `${sRem}s`;
                            };

                            return (
                              <div className="tool-embedding-progress" aria-label={`Embedding progress ${done}/${prog.total}`}>
                                <div className="tep-bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin="0" aria-valuemax="100">
                                  <div className="tep-bar-fill" style={{ width: `${pct}%` }} />
                                </div>
                                <div className="tep-stats">
                                  <span className="tep-counts"><i className="fas fa-list-ol"></i> {done} done • {remaining} remaining • {prog.total} total</span>
                                  <span className="tep-times"><i className="far fa-clock"></i> elapsed {fmt(elapsedMs)} • eta {fmt(etaMs)}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="tool-actions">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleInitiateEmbedding(tool)}
                          disabled={
                            loading ||
                            embeddingLoading ||
                            hasActiveEmbeddingRequest(tool.id) ||
                            isNonEmbeddingToolKind(tool.tool_kind)
                          }
                          title={
                            isNonEmbeddingToolKind(tool.tool_kind)
                              ? 'Embedding applies to vector search tools only'
                              : undefined
                          }
                        >
                          <i className="fas fa-play"></i> Initiate Embedding
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleRemoveEmbeddingsClick(tool)}
                          disabled={
                            loading ||
                            embeddingLoading ||
                            removingEmbeddingsId === tool.id ||
                            isNonEmbeddingToolKind(tool.tool_kind) ||
                            !(tool.ingestion_group_slug || tool.group || tool.group_slug)
                          }
                          title={
                            isNonEmbeddingToolKind(tool.tool_kind)
                              ? `${toolKindLabel(tool.tool_kind)} tools have no vector embeddings`
                              : !(tool.ingestion_group_slug || tool.group || tool.group_slug)
                                ? 'Set an ingestion group first'
                                : 'Delete stored vectors and embedding requests for this tool in this group'
                          }
                        >
                          <i className="fas fa-eraser"></i> Remove embeddings
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditTool(tool)}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteClick(tool.id || tool.slug)}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Bottom Pagination Controls */}
                {pagination.totalPages > 1 && (
                  <PaginationNav
                    position="bottom"
                    page={pagination.totalPages > 0 ? pagination.page + 1 : 1}
                    pageSize={pageSize}
                    total={pagination.totalEntries}
                    allowedPageSizes={[10, 20, 50, 100]}
                    disabled={loading || pagination.totalPages === 0}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                )}
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No tools configured yet. Use the button above to create your first tool.</p>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal show">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Confirm Delete</h2>
                <span className="close" onClick={handleDeleteCancel}>&times;</span>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this tool?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove embeddings confirmation */}
        {showRemoveEmbeddingsConfirm && toolToRemoveEmbeddings && (
          <div className="modal show">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Remove embeddings</h2>
                <span className="close" onClick={handleRemoveEmbeddingsCancel}>&times;</span>
              </div>
              <div className="modal-body">
                <p>Are you sure?</p>
                <p>
                  This removes stored vectors and embedding queue rows for{' '}
                  <strong>{toolToRemoveEmbeddings.name || 'this tool'}</strong>
                  {toolToRemoveEmbeddings.ingestion_group_slug ? (
                    <> in group <strong>{toolToRemoveEmbeddings.ingestion_group_slug}</strong></>
                  ) : null}
                  . The tool definition is kept; run <em>Initiate Embedding</em> again to rebuild vectors.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleRemoveEmbeddingsCancel}
                  disabled={removingEmbeddingsId === toolToRemoveEmbeddings.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleRemoveEmbeddingsConfirm}
                  disabled={removingEmbeddingsId === toolToRemoveEmbeddings.id}
                >
                  {removingEmbeddingsId === toolToRemoveEmbeddings.id ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Removing…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-eraser"></i> Remove embeddings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tool Editor Modal */}
        <ToolEditor
          isOpen={showModal}
          onClose={handleCloseModal}
          onSave={handleSaveTool}
          tool={editingTool}
          loading={loading}
          existingToolSlugs={tools.map((t) => t.slug).filter(Boolean)}
        />
      </div>
    </div>
  );
}

export default ToolsPage;